import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient } from '../api.js';
import { ContextActions } from '../components/ContextActions.jsx';

/**
 * Feedback view — aggregates the CSAT ratings + comments users
 * leave on their resolved help threads.
 *
 * The data lives on the existing HelpRequest rows (rating,
 * rating_comment, rating_at) — surfacing it here gives operators
 * a single page to read what users actually thought without
 * paging through every thread in the inbox.
 *
 * Headline numbers come from the same /v1/admin/help/stats endpoint
 * the inbox uses, so the figures here always match the StatsBar.
 */
const STAR_FILTERS = [
  { id: 'all',   label: 'All ratings' },
  { id: 'low',   label: '1-2 ★', tip: 'Detractors — the most urgent to read' },
  { id: 'mid',   label: '3 ★',   tip: 'Lukewarm — worth a look' },
  { id: 'high',  label: '4-5 ★', tip: 'Promoters' },
];

/** Project-scoped variant — locks the feedback view to one project.
 *  Mounted at /projects/:slug/help/feedback so per-project leads see
 *  only the ratings/comments their users left.  The inbox has the
 *  same pattern; pairing them keeps the navigation predictable. */
export function ProjectHelpFeedback() {
  const { project } = useOutletContext() || {};
  return <HelpFeedback lockedProjectId={project?.project_id || null} />;
}

export function HelpFeedback({ lockedProjectId = null } = {}) {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pull the broad inbox (any status — ratings live on resolved
      // threads but a re-opened thread still carries its old rating).
      // The endpoint defaults to status-agnostic + 100 rows, plenty
      // at the v1 scale.  Project-scope filter passes through to both
      // sub-queries so the headline numbers match the listed comments.
      const qs = lockedProjectId
        ? `?project_id=${encodeURIComponent(lockedProjectId)}`
        : '';
      const statsQs = lockedProjectId
        ? `?days=${days}&project_id=${encodeURIComponent(lockedProjectId)}`
        : `?days=${days}`;
      const [inbox, sts] = await Promise.all([
        api(`/v1/admin/help/requests${qs}`),
        api(`/v1/admin/help/stats${statsQs}`),
      ]);
      const rows = inbox?.items || [];
      const rated = rows
        .filter((r) => (r.rating || 0) > 0)
        .sort((a, b) => (b.rating_at || '').localeCompare(a.rating_at || ''));
      setItems(rated);
      setStats(sts?.data || sts || null);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [api, days, lockedProjectId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      if (filter === 'low')  return r.rating <= 2;
      if (filter === 'mid')  return r.rating === 3;
      if (filter === 'high') return r.rating >= 4;
      return true;
    });
  }, [items, filter]);

  const actions = useMemo(() => {
    const out = [];
    if (!loading && stats?.rated === 0) {
      out.push({
        id: 'no-ratings',
        title: 'No ratings captured yet',
        body: 'The widget asks users to rate a conversation right after you mark it resolved. Close a few open tickets and the feedback will start flowing in.',
        cta: 'Open inbox',
        to: lockedProjectId ? `/projects/${lockedProjectId}/help/inbox` : '/help',
        tone: 'normal',
      });
    } else if (stats?.csat_percent != null && stats.csat_percent < 70 && stats.rated >= 3) {
      out.push({
        id: 'low-csat',
        title: `CSAT is ${stats.csat_percent.toFixed(0)}% — under the 70% threshold`,
        body: 'Read the 1-2★ feedback below first. Patterns there usually point at one or two missing FAQ articles.',
        cta: 'Filter to low ratings',
        onClick: () => setFilter('low'),
        tone: 'warn',
      });
    }
    return out;
  }, [loading, stats, lockedProjectId]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Feedback</h1>
          <p className="page__subtitle">
            Every rating + comment users have left on their conversations
            in the last <strong>{days} days</strong>. The widget shows the
            prompt the moment you mark a thread resolved, so this is the
            unfiltered "how did we do?" voice.
            <br/>
            Reply or re-open from the <Link to={lockedProjectId ? `/projects/${lockedProjectId}/help/inbox` : '/help'}>Help inbox</Link>.
          </p>
        </div>
      </div>

      <ContextActions actions={actions} />

      {stats && (
        <div className="feedback-stats">
          <FeedbackStat label="CSAT" value={stats.csat_percent != null ? `${stats.csat_percent.toFixed(0)}%` : '—'} sub={`% rated 4 or 5`} accent={stats.csat_percent != null && stats.csat_percent >= 80 ? 'green' : 'neutral'} />
          <FeedbackStat label="Avg rating" value={stats.avg_rating != null ? `${stats.avg_rating.toFixed(1)} ★` : '—'} sub={`${stats.rated || 0} responses`} />
          <FeedbackStat label="Responded" value={stats.resolved > 0 ? `${Math.round((stats.rated / stats.resolved) * 100)}%` : '—'} sub={`of resolved threads rated`} />
          <FeedbackBars distribution={stats.rating_distribution || {}} />
        </div>
      )}

      <div className="feedback-controls">
        <div className="feedback-filters" role="tablist" aria-label="Rating filter">
          {STAR_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={filter === f.id}
              title={f.tip}
              className={'pill ' + (filter === f.id ? 'pill--active' : '')}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          className="select"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Window"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading && <div className="state"><div className="state__title">Loading…</div></div>}
      {error && !loading && <div className="state state--error"><div className="state__title">Couldn't load feedback</div><div>{error}</div></div>}

      {!loading && filtered.length === 0 && (
        <div className="state">
          <div className="state__title">Nothing here</div>
          <div>{items.length === 0 ? 'No ratings have come in yet.' : 'No ratings match the current filter.'}</div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <ul className="feedback-list">
          {filtered.map((r) => (
            <li key={r.request_id} className={`feedback-row feedback-row--${ratingTone(r.rating)}`}>
              <div className="feedback-row__head">
                <span className="feedback-row__stars">
                  {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                </span>
                <span className="feedback-row__who">{r.user_email || r.user_id}</span>
                <span className="feedback-row__where">on <code>{r.page_title || r.route}</code></span>
                <span className="feedback-row__when">{formatRelative(r.rating_at || r.updated_at)}</span>
                <Link
                  to={lockedProjectId ? `/projects/${lockedProjectId}/help/inbox#${r.request_id}` : `/help#${r.request_id}`}
                  className="feedback-row__open"
                >
                  Open thread →
                </Link>
              </div>
              {r.rating_comment ? (
                <blockquote className="feedback-row__comment">"{r.rating_comment}"</blockquote>
              ) : (
                <div className="feedback-row__no-comment">No comment.</div>
              )}
              <div className="feedback-row__meta">
                <span>{r.excerpt}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <style>{CSS}</style>
    </div>
  );
}

function FeedbackStat({ label, value, sub, accent = 'neutral' }) {
  return (
    <div className={`feedback-stat feedback-stat--${accent}`}>
      <div className="feedback-stat__value">{value}</div>
      <div className="feedback-stat__label">{label}</div>
      {sub && <div className="feedback-stat__sub">{sub}</div>}
    </div>
  );
}

function FeedbackBars({ distribution }) {
  const counts = [5, 4, 3, 2, 1].map((n) => ({ star: n, count: distribution[String(n)] || 0 }));
  const total = counts.reduce((s, c) => s + c.count, 0) || 1;
  return (
    <div className="feedback-bars" aria-label="Rating distribution">
      {counts.map((c) => (
        <div key={c.star} className="feedback-bar-row">
          <span className="feedback-bar__label">{c.star}★</span>
          <div className="feedback-bar__track">
            <div
              className={'feedback-bar__fill feedback-bar__fill--' + (c.star >= 4 ? 'good' : c.star === 3 ? 'mid' : 'bad')}
              style={{ width: `${(c.count / total) * 100}%` }}
            />
          </div>
          <span className="feedback-bar__count">{c.count}</span>
        </div>
      ))}
    </div>
  );
}

function ratingTone(r) {
  if (r <= 2) return 'low';
  if (r === 3) return 'mid';
  return 'high';
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const min = Math.round(diff / 60_000);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const d = Math.round(hr / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
}

const CSS = `
  .feedback-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) 1.5fr;
    gap: 12px;
    margin-bottom: 16px;
  }
  @media (max-width: 880px) { .feedback-stats { grid-template-columns: repeat(2, 1fr); } }
  .feedback-stat { background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 10px; padding: 12px 14px; }
  .feedback-stat--green .feedback-stat__value { color: #047857; }
  .feedback-stat__value { font-size: 20px; font-weight: 700; line-height: 1.15; }
  .feedback-stat__label { font-size: 11.5px; font-weight: 600; color: var(--text-2, #334155); text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }
  .feedback-stat__sub { font-size: 11px; color: var(--text-3, #64748b); margin-top: 2px; }
  .feedback-bars { background: var(--surface-0, #fff); border: 1px solid var(--border-1, #e5e7eb); border-radius: 10px; padding: 10px 14px; display: flex; flex-direction: column; gap: 4px; }
  .feedback-bar-row { display: flex; align-items: center; gap: 8px; font-size: 12px; }
  .feedback-bar__label { width: 22px; color: var(--text-3, #64748b); font-weight: 600; }
  .feedback-bar__track { flex: 1; height: 8px; background: var(--surface-1, #f1f5f9); border-radius: 9999px; overflow: hidden; }
  .feedback-bar__fill { height: 100%; }
  .feedback-bar__fill--good { background: #10b981; }
  .feedback-bar__fill--mid  { background: #f59e0b; }
  .feedback-bar__fill--bad  { background: #ef4444; }
  .feedback-bar__count { width: 24px; text-align: right; color: var(--text-3, #64748b); font-variant-numeric: tabular-nums; }

  .feedback-controls { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
  .feedback-filters { display: flex; gap: 6px; }

  .feedback-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
  .feedback-row {
    background: var(--surface-0, #fff);
    border: 1px solid var(--border-1, #e5e7eb);
    border-left: 3px solid var(--accent, #94a3b8);
    border-radius: 10px;
    padding: 12px 14px;
  }
  .feedback-row--low  { --accent: #ef4444; }
  .feedback-row--mid  { --accent: #f59e0b; }
  .feedback-row--high { --accent: #10b981; }
  .feedback-row__head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 12.5px; color: var(--text-3, #64748b); }
  .feedback-row__stars { color: #f59e0b; font-size: 14px; letter-spacing: 1px; }
  .feedback-row__who { font-weight: 600; color: var(--text-1, #0f172a); }
  .feedback-row__where code { font-size: 11.5px; padding: 1px 5px; background: var(--surface-1, #f1f5f9); border-radius: 4px; }
  .feedback-row__when { color: var(--text-3, #64748b); }
  .feedback-row__open { margin-left: auto; color: var(--brand-600, #4f46e5); text-decoration: none; font-weight: 600; }
  .feedback-row__open:hover { text-decoration: underline; }
  .feedback-row__comment { margin: 8px 0 6px; padding: 8px 12px; border-left: 3px solid var(--border-1, #e5e7eb); color: var(--text-1, #0f172a); font-size: 13.5px; line-height: 1.5; font-style: italic; }
  .feedback-row__no-comment { margin: 8px 0 6px; font-size: 12px; color: var(--text-3, #94a3b8); font-style: italic; }
  .feedback-row__meta { font-size: 11.5px; color: var(--text-3, #64748b); }
`;
